/* eslint-disable no-param-reassign */
/* eslint-disable object-curly-newline */
/* eslint-env jquery */

import { Component, registerComponent } from 'js/component.js';
import { isEditModeFooter, setDataAttr, uuid, getViewportSize } from 'js/helpers.js';
import { scrollToAnchor } from 'js/ra.js';

const componentName = 'product-browser';
const productDetailServletEndpoint = '/bin/rockwell-automation/product-details';
let cancelBounce = false;
class ProductBrowser extends Component {
    elements = {
        catalogNumberInput: this.element.querySelector(`.${componentName}__catalog-number-input`),
        resultContentObj: this.element.querySelector(`.${componentName}__result-content`),
        htmlContentObj: this.element.querySelector(`.${componentName}__matching-urls`),
        catalogNumberHintObj: this.element.querySelector(`.${componentName}__catalog-number-hint`),
        categoryContainer: this.element.querySelector(`.${componentName}__category-container`),
        quickCatalogFinder: this.element.querySelector(`.${componentName}__quick-catalog-finder`),
        selectedUrls: this.element.querySelector(`.${componentName}__selected-urls`),
        toggleAllButton: this.element.querySelector(`.${componentName}__toggle-all`),
        clearAllButton: this.element.querySelector(`.${componentName}__clear-all`),
    };

    state = {
        selectedCategories: new Map(),
        lastPrefixLength: 0,
        categoryJsonData: null,
        serializedTrie: '',
        parsedTrie: null,
        uidUrlMap: {},
    };

    constants = {
        excludeUrlFilters: [/\/1-default-level.*/, /\/bu-other/],
    };

    shouldExclude = (url) => this.constants.excludeUrlFilters.some((regex) => regex.test(url));

    renderCategories = (categories, container) => {
        categories.forEach((category) => {
            if (this.shouldExclude(category.url)) return;

            const categoryElement = document.createElement('div');
            categoryElement.classList.add(`${componentName}__category`);
            categoryElement.classList.add(`${componentName}__multi-column-container-item`);
            categoryElement.textContent = `${category.name}   |   (${category.numberOfChildProduct || 0})${category.numberOfChildCategory ? `   |   [${category.numberOfChildCategory}]` : ''}`;
            categoryElement.dataset.id = category.id;
            categoryElement.dataset.url = category.url;

            if (category.subcategory && category.subcategory.length > 0) {
                categoryElement.classList.add(`${componentName}__has-children`);
            } else {
                categoryElement.classList.add(`${componentName}__no-children`);
            }

            categoryElement.addEventListener('click', (event) => {
                if (event.ctrlKey || event.metaKey) { // Ctrl for Windows/Linux, Cmd for Mac
                    this.toggleSelection(category, categoryElement);
                } else {
                    this.toggleSubcategories(category, categoryElement);
                }
                event.stopPropagation();
            });
            container.appendChild(categoryElement);
        });
    };

    toggleSubcategories = (category, element) => {
        const existingSubcategories = element.querySelector(`.${componentName}__line`);
        if (existingSubcategories) {
            element.removeChild(existingSubcategories);
            element.classList.remove(`${componentName}__expanded`);
            element.classList.remove(`${componentName}__prefix-match`);
        } else if (category.subcategory && category.subcategory.length > 0) {
            const subcategoriesContainer = document.createElement('div');
            subcategoriesContainer.classList.add(`${componentName}__line`);
            this.renderCategories(category.subcategory, subcategoriesContainer);
            element.appendChild(subcategoriesContainer);
            element.classList.add(`${componentName}__expanded`);
        }
    };

    toggleSelection = (category, element) => {
        const { url } = category;
        if (this.state.selectedCategories.has(url)) {
            this.state.selectedCategories.delete(url);
            element.classList.remove(`${componentName}__selected`);
            this.removeFromSelectedList(url);
        } else {
            this.state.selectedCategories.set(url, category.name);
            element.classList.add(`${componentName}__selected`);
            this.addToSelectedList(url, category.name);
        }
    };
    escapeSelector = (selector) => selector.replace(/([ !"#$%&'()*+,.\/:;<=>?@[\\\]^`{|}~])/g, '\\$1');

    addToSelectedList = (url, name) => {
        const selectedList = this.elements.selectedUrls;
        const listItem = document.createElement('li');
        listItem.textContent = name;
        listItem.dataset.url = url;
        listItem.classList.add(`${componentName}__selected-item`);
        listItem.addEventListener('click', () => {
            this.state.selectedCategories.delete(url);
            listItem.remove();
            const categoryElements = document.querySelectorAll(`[data-url="${url}"]`);
            categoryElements.forEach((element) => element.classList.remove(`${componentName}__selected`));
        });
        selectedList.appendChild(listItem);
    };

    removeFromSelectedList = (url) => {
        const selectedList = this.elements.selectedUrls;
        const listItem = selectedList.querySelector(`[data-url="${url}"]`);
        if (listItem) {
            listItem.remove();
        }
    };

    openAllCategories = (categories, container) => {
        categories.forEach((category) => {
            if (this.shouldExclude(category.url)) return;
            const categoryElement = container.querySelector(`[data-id="${category.id}"]`);
            if (categoryElement && category.subcategory && category.subcategory.length > 0) {
                if (!categoryElement.classList.contains(`${componentName}__expanded`)) {
                    this.toggleSubcategories(category, categoryElement);
                }
                this.openAllCategories(category.subcategory, container);
            }
        });
    };

    closeAllCategories = (categories, container) => {
        if (categories && container) {
            categories.forEach((category) => {
                if (this.shouldExclude(category.url)) return;
                const categoryElement = container.querySelector(`[data-id="${category.id}"]`);
                if (categoryElement && category.subcategory && category.subcategory.length > 0) {
                    if (categoryElement.classList.contains(`${componentName}__expanded`)) {
                        this.toggleSubcategories(category, categoryElement);
                    }
                    this.closeAllCategories(category.subcategory, container);
                }
            });
        }
    };

    // Function to parse serialized trie into a nested object
    parseSerializedTrie = (serializedTrie) => {
        const trie = {};
        const entries = serializedTrie.split(';').filter(Boolean);

        for (const entry of entries) {
            const [path, uids] = entry.split(':');
            let node = trie;
            for (const char of path) {
                if (!node[char]) {
                    node[char] = {};
                }
                node = node[char];
            }
            node.urls = new Set(uids.split(',').map(Number));
        }

        return trie;
    };

    // Function to find matching URLs for a given prefix
    findMatchingUrls = (prefix, trie) => {
        let node = trie;
        for (const char of prefix.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()) {
            if (!node[char]) {
                return [];
            }
            node = node[char];
        }

        // Collect all URLs from the current node
        const urls = new Set();
        const collectUrls = (node) => {
            if (node.urls) {
                for (const uid of node.urls) {
                    urls.add(uid);
                }
            }
            for (const key in node) {
                if (key !== 'urls') {
                    collectUrls(node[key]);
                }
            }
        };
        collectUrls(node);

        return Array.from(urls).map((uid) => this.state.uidUrlMap[uid]);
    };

    // Function to find the first full catalog number for a given prefix
    findFirstFullCatalogNumber = (prefix, trie) => {
        let node = trie;
        prefix = prefix.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

        // Traverse the trie up to the end of the prefix
        for (const char of prefix) {
            if (!node[char]) {
                return null;
            }
            node = node[char];
        }

        // Find the first full catalog number by traversing the remaining trie
        const findFullNumber = (currentNode, currentPrefix) => {
            if (currentNode.urls) {
                return currentPrefix;
            }
            for (const char in currentNode) {
                if (char !== 'urls') {
                    const result = findFullNumber(currentNode[char], currentPrefix + char);
                    if (result) {
                        return result;
                    }
                }
            }
            return null;
        };

        return findFullNumber(node, prefix);
    };

    // return true/false depending on whether the catalog number prefix has children
    fertileCatalogNumber = (prefix, trie) => {
        let node = trie;
        prefix = prefix.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

        // Traverse the trie up to the end of the prefix
        for (const char of prefix) {
            if (!node[char]) {
                return false;
            }
            node = node[char];
        }

        // Check if the node at the end of the prefix has children
        for (const key in node) {
            if (key !== 'urls') {
                return true;
            }
        }

        return false;
    };

    // Debounce function to limit the rate at which a function can fire
    debounce = (func, delay) => {
        let debounceTimer;
        return function() {
            const context = this;
            const args = arguments;

            // Check if cancelBounce is true
            if (cancelBounce) {
                // Reset cancelBounce to false and return without setting the timeout
                cancelBounce = false;
                return;
            }

            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => func.apply(context, args), delay);
        };
    };

    searchCategories = (categories, query) => {
        const matches = [];
        query = query.toLowerCase(); // Convert query to lower case for case-insensitive comparison

        const recurse = (category) => {
            if (category.name.toLowerCase().includes(query)) {
                matches.push([category.url, category.name]);
            }
            if (category.subcategory && category.subcategory.length > 0) {
                category.subcategory.forEach((subcategory) => {
                    recurse(subcategory); // Recurse into subcategories
                });
            }
        };

        categories.forEach((category) => {
            recurse(category); // Start recursion for each top-level category
        });
        return matches;
    };

    // Function to handle the search and update the UI
    performSearch = () => {
        cancelBounce = true;
        const query = this.elements.catalogNumberInput.value.trim();
        if (!query || query.length == 0) {
            return;
        }
        const results = this.searchCategories(this.state.categoryJsonData, query);
        if (results.length > 0) {
            const urls = results.map((item) => item[0]);
            this.openMatchingUrls(urls);
            if (this.elements.catalogNumberHintObj) {
                let foundHint = false;
                for (let i = 0; i < results.length; i++) {
                    if (!foundHint) {
                        const hint = results[i][1];
                        if (query.toLowerCase() == hint.toLowerCase().substring(0, query.length)) {
                            this.elements.catalogNumberHintObj.textContent = query + hint.substring(query.length);
                            foundHint = true;
                        }
                    }
                }
            }
        } else {
            cancelBounce = false;
            if (this.elements.catalogNumberHintObj) {
                this.elements.catalogNumberHintObj.textContent = '';
            }
        }
    };

    openMatchingUrls = (matchingUrls) => {
        let htmlContent = '';
        let resultContent = '';
        if (typeof this.state.parsedTrie === 'undefined') {
            this.state.parsedTrie = this.parseSerializedTrie(this.state.serializedTrie);
        }
        if (matchingUrls.length > 0 && this.state.parsedTrie) {
            htmlContent += '<ul>';
            const selectedCategoriesSet = new Set();
            for (let i = 0; i < matchingUrls.length; i++) {
                htmlContent += '<li>';
                htmlContent += matchingUrls[i];
                const splitSegment = matchingUrls[i].split('/');
                const subPaths = [];
                for (let j = 0; j < splitSegment.length; j++) {
                    if(splitSegment[j] != null && splitSegment[j] != '') {
                        subPaths.push(splitSegment[j]);
                        selectedCategoriesSet.add(`/${subPaths.join('/')}`);
                    }
                }
                htmlContent += '</li>';
            }
            htmlContent += '</ul>';
            htmlContent += '<hr>';
            const selectedCategoriesArray = Array.from(selectedCategoriesSet);

            selectedCategoriesArray.sort((a, b) => {
                // Compare by number of slashes first
                const aSlashCount = (a.match(/\//g) || []).length;
                const bSlashCount = (b.match(/\//g) || []).length;

                if (aSlashCount !== bSlashCount) {
                    return aSlashCount - bSlashCount;
                }

                // If number of slashes is the same, sort alphabetically
                return a.localeCompare(b);
            });

            resultContent = '<ul>';
            // Iterate through the sorted array
            selectedCategoriesArray.forEach((path) => {
                resultContent += '<li>';
                resultContent += path;

                // Select the category-container div
                if (this.elements.categoryContainer) {
                    // Find all divs with class 'category' and data-url equal to the path
                    const escapedPath = this.escapeSelector(path);
                    const categoryDivs = this.elements.categoryContainer.querySelectorAll(`div.${componentName}__category[data-url="${escapedPath}"]`);
                    // Trigger click event on each matching div
                    const matchingItems = 0;
                    categoryDivs.forEach((div) => {
                        div.click();
                        div.classList.add(`${componentName}__prefix-match`);
                    });
                }
                resultContent += '</li>';
            });
            if (selectedCategoriesArray.length > 0) {
                this.elements.categoryContainer.classList.add(`${componentName}__prefix-match`);
            } else {
                this.elements.categoryContainer.classList.remove(`${componentName}__prefix-match`);
            }
            resultContent += '</ul>';
        } else {
            resultContent = '';
            htmlContent = '<p>No matching URLs found</p>';
            this.closeAllCategories(this.state.categoryJsonData, this.elements.categoryContainer);
        }

        if (this.elements.resultContentObj) {
            this.elements.resultContentObj.innerHTML = resultContent;
        }
        if (this.elements.htmlContentObj) {
            this.elements.htmlContentObj.innerHTML = htmlContent;
        }

    };
    handleInput = () => {
        if (cancelBounce) {
            return;
        }
        this.closeAllCategories(this.state.categoryJsonData, this.elements.categoryContainer);
        let prefix = this.value;
        if (typeof prefix === 'undefined') {
            prefix = this.elements.catalogNumberInput.value;
        }
        if (prefix.length > 0) {
            this.state.parsedTrie = this.parseSerializedTrie(this.state.serializedTrie);
            const matchingUrls = this.findMatchingUrls(prefix, this.state.parsedTrie);
            this.openMatchingUrls(matchingUrls);
            const firstFullCatalogNumber = this.findFirstFullCatalogNumber(prefix, this.state.parsedTrie);
            if (firstFullCatalogNumber && firstFullCatalogNumber.toUpperCase().startsWith(prefix.replace(/[^a-zA-Z0-9]/g, '').toUpperCase())) {
                const hint = firstFullCatalogNumber.slice(prefix.replace(/[^a-zA-Z0-9]/g, '').length);
                if (this.elements.catalogNumberHintObj) {
                    this.elements.catalogNumberHintObj.textContent = prefix + hint;
                }
            } else {
                if (this.elements.catalogNumberHintObj) {
                    this.elements.catalogNumberHintObj.textContent = '';
                }
                cancelBounce = false;
                this.performSearch();
            }
        } else {
            if (this.elements.resultContentObj) {
                this.elements.resultContentObj.innerHTML = '';
            }
            if (this.elements.htmlContentObj) {
                this.elements.htmlContentObj.innerHTML = '';
            }
            if (this.elements.catalogNumberHintObj) {
                this.elements.catalogNumberHintObj.textContent = '';
            }
        }
    };

    addEventListeners = () => {
        if (this.elements.catalogNumberInput) {
            // Attach the debounced input handler to the input event
            cancelBounce = false;
            this.elements.catalogNumberInput.addEventListener('input', this.debounce(this.handleInput, 500));

            // Event listener for keydown to handle tab for autocompletion
            this.elements.catalogNumberInput.addEventListener('keydown', (event) => {
                if (event.key === 'Tab') {
                    event.preventDefault();
                    if (this.elements.catalogNumberHintObj) {
                        const hintText = this.elements.catalogNumberHintObj.textContent;
                        if (hintText) {
                            this.elements.catalogNumberInput.value = hintText;
                            cancelBounce = false;
                            this.handleInput();
                            this.elements.catalogNumberHintObj.textContent = '';
                            // Trigger the input event programmatically
                            const inputEvent = new Event('keydown', { bubbles: true });
                            this.elements.catalogNumberInput.dispatchEvent(inputEvent);
                            window.location.hash = this.elements.catalogNumberInput.value;
                        }
                    }
                } else if (event.key === 'Escape') {
                    cancelBounce = true;

                }
            });

            this.elements.catalogNumberInput.addEventListener('change', (event) => {
                window.location.hash = this.elements.catalogNumberInput.value;
            });
        }

        this.elements.toggleAllButton.addEventListener('click', () => {
            if (this.elements.toggleAllButton.textContent === 'Open All') {
                this.openAllCategories(this.state.categoryJsonData, this.elements.categoryContainer);
                this.elements.toggleAllButton.textContent = 'Close All';
            } else {
                this.closeAllCategories(this.state.categoryJsonData, this.elements.categoryContainer);
                this.elements.toggleAllButton.textContent = 'Open All';
            }
        });
        this.elements.clearAllButton.addEventListener('click', () => {
            this.elements.catalogNumberInput.value = '';
            if (this.elements.catalogNumberHintObj) {
                this.elements.catalogNumberHintObj.textContent = '';
            }
            this.closeAllCategories(this.state.categoryJsonData, this.elements.categoryContainer);
            this.elements.catalogNumberInput.focus();
            window.location.hash = '';
            this.elements.categoryContainer.classList.remove(`${componentName}__prefix-match`);
        });
    };

    initDom = async () => {
        try {
            const taxonomyResponse = await fetch(`${productDetailServletEndpoint}?taxonomy=true`);
            this.state.categoryJsonData = await taxonomyResponse.json();
            const serializedTrieResponse = await fetch(`${productDetailServletEndpoint}?serializedTrie=true`);
            const serializedTrieJson = await serializedTrieResponse.json();
            const serializedTrieText = serializedTrieJson.trie;
            this.state.serializedTrie = serializedTrieText.split('"')[1];
            serializedTrieText.split('{')[1].split('}')[0].split(',').forEach((item, index) => {
                this.state.uidUrlMap[(index + 1)] = item.split(':')[1].replaceAll("'", '').trim();
            });
            this.renderCategories(this.state.categoryJsonData, this.elements.categoryContainer);

            if (window.location.hash != null && window.location.hash.length > 1) {
                this.elements.catalogNumberInput.value = decodeURI(window.location.hash.substring(1));
                this.elements.quickCatalogFinder.dispatchEvent(new Event('submit', { bubbles: true }));
                this.elements.catalogNumberInput.dispatchEvent(new Event('input', { bubbles: true }));
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    /**
     * Initialize Component
     */
    init = async () => {
        await this.initDom();
        this.addEventListeners();
    };
}

registerComponent(`.${componentName}`, ProductBrowser);
